import React from 'react';
import PropTypes from 'prop-types';
import { AddPupContainer } from '../AddPupContainer';
import { AddPupPlanSummaryContainer } from '../AddPupPlanSummaryContainer';
import { AddPupSuccessContainer } from '../AddPupSuccessContainer';
import { MealPrepTypeSelectionForm } from '../MealPrepTypeSelectionForm';
import { FreshRecipesSelectionForm } from '../FreshRecipesSelectionForm';
import { DryRecipesSelectionForm } from '../DryRecipesSelectionForm';
import { EditAddressContainer } from '../EditAddressContainer';
import { EditPaymentContainer } from '../EditPaymentContainer';
import { StripeElementsProvider } from '../../payment';
import { interpolate } from '../../cms';
import {
  getStarterBoxDryConfig,
  getStarterBoxFreshConfig,
  getStarterBoxProductTypeConfig,
  getVariantByRecipes,
} from '../../products';
import { EMPTY_PET, getPronoun, PRONOUN_TYPES } from '../../pets';
import { STATE, ACTIONS } from './constants';

const AddPupQuiz = ({ labels, petForm, lastStep, setPetFlow, send }) => {
  return (
    <AddPupContainer
      {...interpolate(labels, {})}
      cancelUrl={lastStep ? `/account/add-pup/${lastStep}` : labels.cancelUrl}
      defaultValues={petForm ?? EMPTY_PET}
      onCancel={() => {
        setPetFlow(null, { skipRender: true });
      }}
      onPetSave={pet => send(ACTIONS.submitPupForm({ pet }))}
    />
  );
};

AddPupQuiz.propTypes = {
  labels: PropTypes.object.isRequired,
  petForm: PropTypes.object,
  setPetFlow: PropTypes.func.isRequired,
  lastStep: PropTypes.string,
  send: PropTypes.func.isRequired,
};

const MealPrep = ({
  labels,
  selectedProductType,
  productTypes,
  petName,
  lastStep,
  send,
}) => {
  const {
    defaultProductType,
    unavailableProductTypes,
  } = getStarterBoxProductTypeConfig(productTypes, null, selectedProductType);

  const freshPrice = productTypes.fresh.recommended?.price_per_week ?? '';
  const bakedPrice = productTypes.dry.recommended?.price_per_week ?? '';
  const mixedPrice = productTypes.mixed.recommended?.price_per_week ?? '';

  return (
    <MealPrepTypeSelectionForm
      {...interpolate(labels, {
        pupName: petName,
        mixedPrice,
        freshPrice,
        bakedPrice,
      })}
      cancelUrl={lastStep ? `/account/add-pup/${lastStep}` : labels.cancelUrl}
      defaultMealPrepType={defaultProductType}
      unavailableMealPrepTypes={unavailableProductTypes}
      onSubmit={mealPrepType =>
        send(ACTIONS.selectedMealPrep({ mealPrepType }))
      }
    />
  );
};

MealPrep.propTypes = {
  labels: PropTypes.object.isRequired,
  productTypes: PropTypes.object.isRequired,
  selectedProductType: PropTypes.string,
  petName: PropTypes.string.isRequired,
  lastStep: PropTypes.string,
  send: PropTypes.func.isRequired,
};

const Fresh = ({
  labels,
  productTypes,
  selectedProductType,
  selectedFreshRecipes,
  petName,
  petForm,
  lastStep,
  send,
}) => {
  const {
    defaultRecipes,
    unavailableRecipes,
    product,
  } = getStarterBoxFreshConfig(
    productTypes[selectedProductType],
    null,
    selectedFreshRecipes,
  );

  return (
    <FreshRecipesSelectionForm
      {...interpolate(labels, {
        pupName: petName,
        pupWeight: petForm.weight,
        pupBreed: petForm.breeds?.map(breed => breed.name || breed.label),
      })}
      cancelTarget={
        lastStep ? `/account/add-pup/${lastStep}/` : labels.cancelTarget
      }
      cancelAction={lastStep ? () => send(ACTIONS.toSummary()) : null}
      unavailableRecipes={unavailableRecipes}
      defaultRecipes={defaultRecipes}
      defaultVariantPrice={product.variants[0].price_per_week}
      productVariants={product.variants}
      onNoRecipesFoundClick={() => send(ACTIONS.toAddPupQuiz())}
      onSubmit={recipes => send(ACTIONS.selectedFreshRecipes({ recipes }))}
    />
  );
};

Fresh.propTypes = {
  labels: PropTypes.object.isRequired,
  productTypes: PropTypes.object.isRequired,
  selectedProductType: PropTypes.string.isRequired,
  selectedFreshRecipes: PropTypes.array,
  petName: PropTypes.string.isRequired,
  petForm: PropTypes.object.isRequired,
  lastStep: PropTypes.string,
  send: PropTypes.func.isRequired,
};

const Baked = ({
  labels,
  productTypes,
  selectedProductType,
  selectedBakedRecipes,
  petName,
  lastStep,
  petForm,
  send,
}) => {
  const {
    defaultRecipes,
    unavailableRecipes,
    product,
  } = getStarterBoxDryConfig(
    productTypes[selectedProductType],
    null,
    selectedBakedRecipes,
  );

  return (
    <DryRecipesSelectionForm
      {...interpolate(labels, {
        pupName: petName,
        pupWeight: petForm.weight,
        pupBreed: petForm.breeds?.map(breed => breed.name || breed.label),
        pupPronoun: getPronoun(petForm.gender, {
          type: PRONOUN_TYPES.POSSESSIVE_ADJECTIVE,
          perspective: 3,
        }),
      })}
      cancelTarget={
        lastStep ? `/account/add-pup/${lastStep}/` : labels.cancelTarget
      }
      cancelAction={lastStep ? () => send(ACTIONS.toSummary()) : null}
      defaultRecipes={defaultRecipes}
      defaultVariantPrice={product.variants[0].price_per_week}
      unavailableRecipes={unavailableRecipes}
      onSubmit={recipes => send(ACTIONS.selectedBakedRecipes({ recipes }))}
    />
  );
};

Baked.propTypes = {
  labels: PropTypes.object.isRequired,
  productTypes: PropTypes.object.isRequired,
  selectedProductType: PropTypes.string.isRequired,
  selectedBakedRecipes: PropTypes.array,
  petName: PropTypes.string.isRequired,
  petForm: PropTypes.shape({
    activity: PropTypes.string.isRequired,
    weight: PropTypes.number.isRequired,
    breeds: PropTypes.array.isRequired,
    gender: PropTypes.string.isRequired,
  }).isRequired,
  lastStep: PropTypes.string,
  send: PropTypes.func.isRequired,
};

const Summary = ({
  labels,
  petForm,
  petName,
  productTypes,
  selectedProductType,
  selectedFreshRecipes,
  selectedBakedRecipes,
  setPetFlow,
  send,
}) => {
  const variants = productTypes[selectedProductType].starterBoxes[0].variants;

  const variant = getVariantByRecipes(variants, {
    fresh: selectedFreshRecipes,
    dry: selectedBakedRecipes,
  });

  return (
    <AddPupPlanSummaryContainer
      {...interpolate(labels, { pupName: petName, pupCalories: 'TODO' })}
      petForm={petForm}
      productSku={variant.sku}
      subscriptionSku={variant.subscription_sku}
      onSubmit={petId => {
        setPetFlow(null, { skipRender: true });
        send(ACTIONS.toSuccess({ petId }));
      }}
      onCancel={() => setPetFlow(null, { skipRender: true })}
      pupInfoTarget={{
        pathname: `/account/add-pup/${STATE.quiz}`,
      }}
      mealPrepTarget={{
        pathname: `/account/add-pup/${STATE.mealprep}`,
      }}
      freshRecipesTarget={{
        pathname: `/account/add-pup/${STATE.fresh}`,
      }}
      bakedRecipesTarget={{
        pathname: `/account/add-pup/${STATE.baked}`,
        query: { edit: `${true}` },
      }}
      paymentTarget={{
        pathname: `/account/add-pup/${STATE.payment}`,
      }}
      deliveryTarget={{
        pathname: `/account/add-pup/${STATE.delivery}`,
      }}
    />
  );
};

Summary.propTypes = {
  labels: PropTypes.object.isRequired,
  petName: PropTypes.string.isRequired,
  petForm: PropTypes.object.isRequired,
  productTypes: PropTypes.object.isRequired,
  selectedProductType: PropTypes.string.isRequired,
  selectedFreshRecipes: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedBakedRecipes: PropTypes.arrayOf(PropTypes.string).isRequired,
  setPetFlow: PropTypes.func.isRequired,
  lastStep: PropTypes.string,
  send: PropTypes.func.isRequired,
};

const Delivery = ({ labels, send }) => {
  return (
    <EditAddressContainer
      {...labels}
      onAddressChanged={() => send(ACTIONS.toSummary())}
    />
  );
};

Delivery.propTypes = {
  labels: PropTypes.object.isRequired,
  send: PropTypes.func.isRequired,
};

const Payment = ({ labels, send }) => {
  return (
    <StripeElementsProvider>
      <EditPaymentContainer
        {...labels}
        onPaymentChanged={() => send(ACTIONS.toSummary())}
      />
    </StripeElementsProvider>
  );
};

Payment.propTypes = {
  labels: PropTypes.object.isRequired,
  send: PropTypes.func.isRequired,
};

const Success = ({ labels, petId }) => {
  return (
    <AddPupSuccessContainer
      {...labels}
      petId={petId}
      urlTarget={`/account/?pet_id=${petId}`}
    />
  );
};

Success.propTypes = {
  labels: PropTypes.object.isRequired,
  petId: PropTypes.string.isRequired,
};

export const components = {
  [STATE.quiz]: AddPupQuiz,
  [STATE.mealprep]: MealPrep,
  [STATE.fresh]: Fresh,
  [STATE.baked]: Baked,
  [STATE.summary]: Summary,
  [STATE.delivery]: Delivery,
  [STATE.payment]: Payment,
  [STATE.success]: Success,
};
